import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { MessageTemplate } from '../types/messageTemplate';
import { stableStringify } from '@Utils/searchParams';

type TemplatesResponse = { data: MessageTemplate[]; meta: Record<string, any> };
type DefaultError = unknown;

type UseTemplatesParams<S extends Record<string, any> = Record<string, any>> = {
  /** Optional query string params to forward to service.getMessageTemplates */
  searchParams?: S;
  /** Override default enabled behavior */
  enabled?: boolean;
};

export function useMessageTemplate<
  TData = TemplatesResponse,
  TError = DefaultError,
>(
  { searchParams, enabled = true }: UseTemplatesParams = {},
  options?: Omit<
    UseQueryOptions<TemplatesResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const stableParams = useMemo(
    () => stableStringify(searchParams),
    [searchParams],
  );

  // console.log('*** searchParams', searchParams, '\nstableParams', stableParams);

  return useQuery<TemplatesResponse, TError, TData>({
    queryKey: ['messageTemplate', stableParams],
    enabled: enabled,
    queryFn: async () => {
      return service.getMessageTemplatesHook(searchParams ?? {});
    },
    ...(options as any),
  });
}
